import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { corporatePartCostService } from 'corporate/services';
import {
  IGetActiveOverlappingCostPayload,
  IRemoveCorporatePartCostPayload,
  IUpsertCorporatePartCostPayload,
} from 'corporate/types';

interface ICorporatePartCostActionsProps {
  position?: NotificationPosition;
  selectionCount?: number;
}

export const useCorporatePartCostActions = ({
  position,
  selectionCount,
}: ICorporatePartCostActionsProps = {}) => {
  return {
    ...useUpsertCorporatePartCost(position),
    ...useRemoveCorporatePartCost(selectionCount, position),
    ...useCheckActiveOverlappingCost(position),
  };
};

const useUpsertCorporatePartCost = (position: NotificationPosition = NotificationPosition.FORM) => {
  const { errors } = useHandleErrors({
    success: (payload: IUpsertCorporatePartCostPayload) => ({
      message: payload?.id ? t('messagePartCostUpdateSuccess') : t('messagePartCostCreateSuccess'),
    }),
    loading: (payload: IUpsertCorporatePartCostPayload) => ({
      message: payload?.id
        ? t('messagePartCostUpdateInProgress')
        : t('messagePartCostCreateInProgress'),
    }),
    error: (payload: IUpsertCorporatePartCostPayload) => ({
      title: payload?.id ? t('errorTitlePartCostUpdate') : t('errorTitlePartCostCreate'),
    }),
    position,
    invalidateQueries: ['corporatePartCostList', 'corporatePart'],
  });

  const { mutateAsync } = useMutation(
    (payload: IUpsertCorporatePartCostPayload) =>
      corporatePartCostService.upsertCorporatePartCost(payload),
    { ...errors }
  );

  return { upsertCorporatePartCost: mutateAsync };
};

const useRemoveCorporatePartCost = (
  selectionCount = 1,
  position: NotificationPosition = NotificationPosition.DRAWER
) => {
  const { errors } = useHandleErrors({
    success: { message: t('messagePartCostsRemoveSuccess', { count: selectionCount }) },
    loading: { message: t('messagePartCostsRemoveInProgress', { count: selectionCount }) },
    error: { title: t('errorTitlePartCostsRemove', { count: selectionCount }) },
    position,
    invalidateQueries: ['corporatePartCostList', 'corporatePart'],
  });

  const { mutateAsync } = useMutation(
    (data: IRemoveCorporatePartCostPayload) =>
      corporatePartCostService.removeCorporatePartCost(data),
    { ...errors }
  );

  return { removeCorporatePartCost: mutateAsync };
};

const useCheckActiveOverlappingCost = (
  position: NotificationPosition = NotificationPosition.FORM
) => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageCheckActiveOverlappingCostInProgress') },
    error: { title: t('errorTitleCheckActiveOverlappingCost') },
    position,
  });

  const { mutateAsync } = useMutation(
    (payload: IGetActiveOverlappingCostPayload) =>
      corporatePartCostService.getActiveOverlappingCost(payload),
    { ...errors }
  );

  return { checkActiveOverlappingCost: mutateAsync };
};
